import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import type {
  BankAccount,
  BankAccountWithBank,
  BankTransaction,
  CreateBankAccountInput,
  UpdateBankAccountInput,
  BankTransactionFilters,
  CreateBankTransactionInput,
  UpdateBankTransactionInput,
} from '@erp/data-access/interfaces';


const BANK_KEY = ['bank-accounts'] as const;
const BANKS_REF_KEY = ['tn-banks'] as const;
const TX_KEY = ['bank-transactions'] as const;

// ---- Bank accounts ----

export function useBankAccounts() {
  const ds = useDataService();
  return useQuery({ queryKey: BANK_KEY, queryFn: () => ds.treasury.listBankAccounts() });
}

export function useBankAccount(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...BANK_KEY, id],
    queryFn: () => ds.treasury.getBankAccountById(id),
    enabled: !!id,
    // List cache holds BankAccount (no `bank` join). Cast it as the
    // detailed shape — the bank info will fill in once the network query
    // resolves; until then the page renders all the non-bank fields.
    placeholderData: () => listCacheFallback<BankAccount>(qc, BANK_KEY, id) as BankAccountWithBank | undefined,
  });
}

export function useCreateBankAccount() {
  const ds = useDataService();
  return useResilientMutation<CreateBankAccountInput, BankAccount>({
    entity: 'bank_accounts',
    op: 'create',
    invalidate: [BANK_KEY],
    online: (input) => ds.treasury.createBankAccount(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        name: input.name,
        bankId: input.bankId ?? null,
        bankName: input.bankName,
        rib: input.rib ?? null,
        address: input.address ?? null,
        currentBalance: input.currentBalance ?? 0,
        isActive: input.isActive ?? true,
        accountType: input.accountType ?? 'current',
        createdAt: new Date().toISOString(),
      }) as unknown as BankAccount,
  });
}

export function useUpdateBankAccount() {
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdateBankAccountInput }, BankAccount>({
    entity: 'bank_accounts',
    op: 'update',
    invalidate: [BANK_KEY],
    online: ({ id, input }) => ds.treasury.updateBankAccount(id, input),
    optimistic: ({ id }) => ({ id, name: '(édité hors-ligne)' }) as unknown as BankAccount,
  });
}

export function useDeleteBankAccount() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'bank_accounts',
    op: 'delete',
    invalidate: [BANK_KEY],
    online: (id) => ds.treasury.deleteBankAccount(id),
    optimistic: () => undefined as unknown as void,
  });
}

// ---- Tunisian banks reference ----

export function useTnBanks() {
  const ds = useDataService();
  return useQuery({
    queryKey: BANKS_REF_KEY,
    queryFn: () => ds.treasury.listBanks(),
    staleTime: 24 * 60 * 60 * 1000, // 24h — référentiel quasi-statique
  });
}

// ---- Bank transactions ----

/**
 * Always fetches all bank transactions and filters in memory so account
 * detail pages and the global tx list both hit the prewarmed cache offline.
 */
export function useBankTransactions(filters?: BankTransactionFilters) {
  const ds = useDataService();
  const query = useQuery({
    queryKey: [...TX_KEY, undefined],
    queryFn: () => ds.treasury.listTransactions(),
  });
  const filtered = useMemo(() => {
    if (!query.data) return query.data;
    if (!filters) return query.data;
    const { accountId, dateFrom, dateTo, transactionType, direction, isReconciled, search } = filters;
    const s = search?.trim().toLowerCase();
    return query.data.filter((t) => {
      if (accountId && t.bankAccountId !== accountId) return false;
      if (dateFrom && t.transactionDate < dateFrom) return false;
      if (dateTo && t.transactionDate > dateTo) return false;
      if (transactionType && t.transactionType !== transactionType) return false;
      if (direction && t.direction !== direction) return false;
      if (isReconciled !== undefined && t.isReconciled !== isReconciled) return false;
      if (s) {
        const hay = `${t.description ?? ''} ${t.reference ?? ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [query.data, filters]);
  return { ...query, data: filtered } as typeof query;
}

export function useBankTransaction(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...TX_KEY, id],
    queryFn: () => ds.treasury.getTransactionById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<BankTransaction>(qc, TX_KEY, id),
  });
}

export function useCreateTransaction() {
  const ds = useDataService();
  return useResilientMutation<CreateBankTransactionInput, BankTransaction>({
    entity: 'bank_transactions',
    op: 'create',
    invalidate: [TX_KEY, BANK_KEY],
    online: (input) => ds.treasury.createTransaction(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        bankAccountId: input.bankAccountId,
        transactionDate: input.transactionDate,
        transactionType: input.transactionType,
        direction: input.direction,
        amount: input.amount,
        description: input.description ?? null,
        reference: input.reference ?? null,
        isReconciled: input.isReconciled ?? false,
        createdAt: new Date().toISOString(),
      }) as BankTransaction,
    applyOptimistic: (qc, tx) => {
      qc.setQueriesData<BankTransaction[]>({ queryKey: TX_KEY }, (old) =>
        Array.isArray(old) && !old.some((t) => t.id === tx.id) ? [tx, ...old] : old,
      );
    },
  });
}

export function useUpdateTransaction() {
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdateBankTransactionInput }, BankTransaction>({
    entity: 'bank_transactions',
    op: 'update',
    invalidate: [TX_KEY],
    online: ({ id, input }) => ds.treasury.updateTransaction(id, input),
    optimistic: ({ id, input }) => ({ id, ...input, updatedAt: new Date().toISOString() }) as BankTransaction,
    applyOptimistic: (qc, _r, { id, input }) => {
      qc.setQueriesData<BankTransaction[]>({ queryKey: TX_KEY }, (old) =>
        Array.isArray(old) ? old.map((t) => (t.id === id ? ({ ...t, ...input } as BankTransaction) : t)) : old,
      );
    },
  });
}

export function useDeleteTransaction() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'bank_transactions',
    op: 'delete',
    invalidate: [TX_KEY, BANK_KEY],
    online: (id) => ds.treasury.deleteTransaction(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<BankTransaction[]>({ queryKey: TX_KEY }, (old) =>
        Array.isArray(old) ? old.filter((t) => t.id !== id) : old,
      );
    },
  });
}

export function useReconcileTransactions() {
  const ds = useDataService();
  return useResilientMutation<{ ids: string[]; reconciled: boolean }, void>({
    entity: 'bank_transactions',
    op: 'custom',
    method: 'reconcile',
    invalidate: [TX_KEY],
    online: ({ ids, reconciled }) => ds.treasury.reconcileTransactions(ids, reconciled),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, { ids, reconciled }) => {
      const idSet = new Set(ids);
      qc.setQueriesData<BankTransaction[]>({ queryKey: TX_KEY }, (old) =>
        Array.isArray(old) ? old.map((t) => (idSet.has(t.id) ? { ...t, isReconciled: reconciled } : t)) : old,
      );
    },
  });
}

export function useRecalculateBalance() {
  const ds = useDataService();
  return useResilientMutation<string, number>({
    entity: 'bank_transactions',
    op: 'custom',
    method: 'recalculateBalance',
    invalidate: [BANK_KEY],
    online: (accountId) => ds.treasury.recalculateBalance(accountId),
    optimistic: () => 0,
  });
}
